import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ScrollView, useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';
import { SccPressable } from '@/components/SccPressable';
import { DESKTOP_BREAKPOINT } from '../constants/layout';
import { useEditMode } from '../context/EditModeContext';
export default function StickyTabHeader({ sections, activeSection, onTabPress, topOffset = 0, }) {
    var _a;
    const { width: windowWidth } = useWindowDimensions();
    const isDesktop = windowWidth >= DESKTOP_BREAKPOINT;
    const [isSticky, setIsSticky] = useState(false);
    const sentinelRef = useRef(null);
    const scrollViewRef = useRef(null);
    const tabPositions = useRef(new Map());
    const editContext = useEditMode();
    const isEditMode = (_a = editContext === null || editContext === void 0 ? void 0 : editContext.isEditMode) !== null && _a !== void 0 ? _a : false;
    // Sticky 상태 감지
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const sentinel = sentinelRef.current;
        if (!sentinel)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            // sentinel이 화면에서 사라지면 sticky 상태
            setIsSticky(!entry.isIntersecting);
        }, { threshold: 0 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);
    // Active 탭이 바뀔 때 탭 바 내에서만 스크롤 (페이지 스크롤에 영향 없음)
    useEffect(() => {
        const tabPos = tabPositions.current.get(activeSection);
        const scrollView = scrollViewRef.current;
        if (!tabPos || !scrollView)
            return;
        // 탭을 중앙에 오도록 스크롤 위치 계산
        const scrollViewWidth = windowWidth;
        const targetScrollX = tabPos.left - (scrollViewWidth / 2) + (tabPos.width / 2);
        scrollView.scrollTo({
            x: Math.max(0, targetScrollX),
            animated: true,
        });
    }, [activeSection, windowWidth]);
    if (sections.length === 0) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: sentinelRef, style: { height: 1, marginBottom: -1 } }), _jsx(Container, { isDesktop: isDesktop, isSticky: isSticky, topOffset: topOffset, children: _jsx(TabsScrollView, { ref: scrollViewRef, horizontal: true, showsHorizontalScrollIndicator: false, contentContainerStyle: {
                        justifyContent: 'center',
                        flexGrow: 1,
                        paddingHorizontal: 16,
                    }, children: _jsx(TabsContainer, { children: sections.map((section) => {
                            const isActive = section.id === activeSection;
                            return (_jsx(View, { onLayout: (e) => {
                                    const { x, width } = e.nativeEvent.layout;
                                    tabPositions.current.set(section.id, { left: x, width });
                                }, children: _jsx(SccPressable, { onPress: () => onTabPress(section.id), elementName: "bbucle-road-sticky-tab", trackView: true, logParams: { tabId: section.id, tabLabel: section.label, isDesktop }, disableLogging: isEditMode, children: _jsxs(TabButtonContent, { isDesktop: isDesktop, children: [_jsx(TabLabel, { isActive: isActive, isDesktop: isDesktop, children: section.label }), isActive && _jsx(ActiveIndicator, {})] }) }) }, section.id));
                        }) }) }) })] }));
}
const Container = styled(View) `
  position: sticky;
  top: ${({ topOffset }) => topOffset}px;
  z-index: 100;
  background-color: white;
  padding-top: ${({ isDesktop }) => (isDesktop ? '16px' : '4px')};
  border-bottom-width: ${({ isSticky }) => (isSticky ? '1px' : '0')};
  border-bottom-color: #e5e5e5;
  transition: background-color 0.2s ease, border-bottom-width 0.2s ease;
`;
const TabsScrollView = styled(ScrollView) `
  flex-direction: row;
`;
const TabsContainer = styled(View) `
  flex-direction: row;
`;
const TabButtonContent = styled(View) `
  padding: ${({ isDesktop }) => (isDesktop ? '8px 20px' : '8px 16px')};
  align-items: center;
`;
const TabLabel = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '18px' : '16px')};
  font-weight: 500;
  color: ${({ isActive }) => (isActive ? '#000000' : '#999999')};
  letter-spacing: -0.4px;
`;
const ActiveIndicator = styled(View) `
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #000000;
`;
